import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, BellRing, Plus, Trash2, RefreshCw, TrendingUp, TrendingDown, ArrowUpDown, Wifi, WifiOff, Check, X, Settings } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import NotificationSettings from "@/components/NotificationSettings";

const ICONS = {
  cocoa: "🍫", coffee: "☕", sugar: "🍬", cotton: "🧵", wheat: "🌾", 
  maize: "🌽", soybean: "🫘", palm_oil: "🌴", gold: "🥇", crude_oil: "🛢️",
  copper: "🔶", natural_gas: "🔥", silver: "🥈", zinc: "⚪"
};

export default function PriceAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [currentPrices, setCurrentPrices] = useState({});
  const [activeTab, setActiveTab] = useState("alerts");
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // New alert form
  const [newAlert, setNewAlert] = useState({
    commodity_id: "",
    condition: "above",
    threshold: "",
    note: ""
  });
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch alerts and commodities
  const fetchData = async () => {
    try {
      const [alertsRes, commoditiesRes] = await Promise.all([
        api.get("/price-alerts"),
        api.get("/price-alerts/commodities")
      ]);
      setAlerts(alertsRes.data.alerts || []);
      setCommodities(commoditiesRes.data.commodities || []);
      
      // Build initial prices map
      const prices = {};
      (commoditiesRes.data.commodities || []).forEach(c => {
        prices[c.id] = c.price;
      });
      setCurrentPrices(prices);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  };

  // Connect to WebSocket for real-time price updates
  const connectWebSocket = useCallback(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/prices`);
    
    ws.onopen = () => {
      setWsConnected(true);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "init") {
        const prices = {};
        Object.entries(data.prices).forEach(([id, p]) => {
          prices[id] = p.price;
        });
        setCurrentPrices(prices);
      } 
      else if (data.type === "tick") {
        // Update prices
        setCurrentPrices(prev => {
          const updated = { ...prev };
          data.updates.forEach(u => {
            updated[u.id] = u.price;
          });
          return updated;
        });
        
        // Check for triggered alerts
        if (data.triggered_alerts && data.triggered_alerts.length > 0) {
          data.triggered_alerts.forEach(alert => {
            // Show toast notification
            toast.success(
              <div className="flex items-center gap-3">
                <BellRing className="w-5 h-5 text-[#00ff41]" />
                <div>
                  <p className="font-bold">{alert.commodity_name} Alert!</p>
                  <p className="text-sm">{alert.message}</p>
                </div>
              </div>,
              { duration: 10000 }
            );
            
            // Update local alerts state
            setAlerts(prev => prev.map(a => 
              a.alert_id === alert.alert_id 
                ? { ...a, triggered: true, triggered_at: new Date().toISOString() }
                : a
            ));
          });
        }
      }
    };
    
    ws.onclose = () => {
      setWsConnected(false);
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
    };
    
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
    
    wsRef.current = ws;
  }, []);

  useEffect(() => {
    fetchData();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connectWebSocket]);

  // Create new alert
  const handleCreateAlert = async () => {
    if (!newAlert.commodity_id || !newAlert.threshold) {
      toast.error("Please select a commodity and set a threshold");
      return;
    }
    
    try {
      const { data } = await api.post("/price-alerts", {
        commodity_id: newAlert.commodity_id,
        condition: newAlert.condition,
        threshold: parseFloat(newAlert.threshold),
        note: newAlert.note || null
      });
      
      setAlerts(prev => [data.alert, ...prev]);
      toast.success("Price alert created!");
      setDialogOpen(false);
      setNewAlert({ commodity_id: "", condition: "above", threshold: "", note: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create alert");
    }
  };

  // Delete alert
  const handleDeleteAlert = async (alertId) => {
    try {
      await api.delete(`/price-alerts/${alertId}`);
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success("Alert deleted");
    } catch (err) {
      toast.error("Failed to delete alert");
    }
  };

  // Toggle alert
  const handleToggleAlert = async (alertId) => {
    try {
      const { data } = await api.put(`/price-alerts/${alertId}/toggle`);
      setAlerts(prev => prev.map(a => 
        a.alert_id === alertId ? { ...a, active: data.active, triggered: data.active ? false : a.triggered } : a
      ));
      toast.success(data.message);
    } catch (err) {
      toast.error("Failed to toggle alert");
    }
  };

  // Reset triggered alert
  const handleResetAlert = async (alertId) => {
    try {
      await api.put(`/price-alerts/${alertId}/reset`);
      setAlerts(prev => prev.map(a => 
        a.alert_id === alertId ? { ...a, triggered: false, triggered_at: null, active: true } : a
      ));
      toast.success("Alert reset - will trigger again");
    } catch (err) {
      toast.error("Failed to reset alert");
    }
  };

  const selectedCommodity = commodities.find(c => c.id === newAlert.commodity_id);

  return (
    <div data-testid="price-alerts-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Price Alerts
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            Get notified when commodities hit your target prices
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* WebSocket Status */}
          <div className="flex items-center gap-2">
            {wsConnected ? (
              <>
                <Wifi className="w-4 h-4 text-[#00ff41]" />
                <span className="text-[10px] text-[#00ff41] font-mono uppercase">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-[#ff3b30]" />
                <span className="text-[10px] text-[#ff3b30] font-mono uppercase">Offline</span>
              </>
            )}
          </div>
          
          {/* Create Alert Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-alert-btn"
                className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs gap-2"
              >
                <Plus className="w-4 h-4" /> New Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg uppercase tracking-wider text-zinc-100">
                  Create Price Alert
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Commodity Select */}
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Commodity</label>
                  <Select value={newAlert.commodity_id} onValueChange={(v) => setNewAlert(p => ({ ...p, commodity_id: v }))}>
                    <SelectTrigger data-testid="commodity-select" className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono">
                      <SelectValue placeholder="Select commodity" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none max-h-[300px]">
                      {commodities.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-sm font-mono">
                          <span className="flex items-center gap-2">
                            {ICONS[c.id]} {c.name} - ${c.price.toLocaleString()}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Condition Select */}
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Condition</label>
                  <Select value={newAlert.condition} onValueChange={(v) => setNewAlert(p => ({ ...p, condition: v }))}>
                    <SelectTrigger data-testid="condition-select" className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                      <SelectItem value="above" className="text-sm font-mono">
                        <span className="flex items-center gap-2"><TrendingUp className="w-3 h-3 text-[#00ff41]" /> Price goes above</span>
                      </SelectItem>
                      <SelectItem value="below" className="text-sm font-mono">
                        <span className="flex items-center gap-2"><TrendingDown className="w-3 h-3 text-[#ff3b30]" /> Price goes below</span>
                      </SelectItem>
                      <SelectItem value="crosses" className="text-sm font-mono">
                        <span className="flex items-center gap-2"><ArrowUpDown className="w-3 h-3 text-[#0ea5e9]" /> Price crosses</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Threshold Input */}
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">
                    Threshold Price {selectedCommodity && `(${selectedCommodity.unit})`}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <Input
                      data-testid="threshold-input"
                      type="number"
                      step="0.01"
                      placeholder={selectedCommodity ? selectedCommodity.price.toString() : "0.00"}
                      value={newAlert.threshold}
                      onChange={(e) => setNewAlert(p => ({ ...p, threshold: e.target.value }))}
                      className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono pl-7"
                    />
                  </div>
                  {selectedCommodity && (
                    <p className="text-[10px] text-zinc-600 font-mono mt-1">
                      Current price: ${selectedCommodity.price.toLocaleString()}
                    </p>
                  )}
                </div>
                
                {/* Note Input */}
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Note (optional)</label>
                  <Input
                    data-testid="note-input"
                    placeholder="e.g., Sell position"
                    value={newAlert.note}
                    onChange={(e) => setNewAlert(p => ({ ...p, note: e.target.value }))}
                    className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="rounded-none border-zinc-700 font-mono text-xs"
                >
                  Cancel
                </Button>
                <Button 
                  data-testid="confirm-create-alert"
                  onClick={handleCreateAlert}
                  className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs"
                >
                  Create Alert
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-none p-1">
          <TabsTrigger 
            value="alerts" 
            className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs"
          >
            <Bell className="w-3.5 h-3.5 mr-2" />
            Alerts
          </TabsTrigger>
          <TabsTrigger 
            value="settings" 
            className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs"
          >
            <Settings className="w-3.5 h-3.5 mr-2" />
            Notification Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alerts" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase font-mono">Total Alerts</p>
                <p className="text-2xl font-mono font-bold text-zinc-100">{alerts.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase font-mono">Active</p>
                <p className="text-2xl font-mono font-bold text-[#00ff41]">
                  {alerts.filter(a => a.active && !a.triggered).length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase font-mono">Triggered</p>
                <p className="text-2xl font-mono font-bold text-[#eab308]">
                  {alerts.filter(a => a.triggered).length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase font-mono">Paused</p>
                <p className="text-2xl font-mono font-bold text-zinc-500">
                  {alerts.filter(a => !a.active).length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Alerts List */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Bell className="w-3.5 h-3.5 text-[#00ff41]" />
                Your Price Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin w-6 h-6 border-2 border-[#00ff41] border-t-transparent rounded-full mx-auto" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 font-mono text-sm">No price alerts yet</p>
              <p className="text-zinc-600 font-mono text-xs mt-1">Create your first alert to get notified</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="divide-y divide-zinc-900">
                {alerts.map((alert) => (
                  <div 
                    key={alert.alert_id}
                    data-testid={`alert-${alert.alert_id}`}
                    className={`p-4 flex items-center justify-between gap-4 ${
                      alert.triggered ? "bg-[#eab308]/5" : 
                      !alert.active ? "opacity-50" : ""
                    }`}
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <span className="text-2xl">{ICONS[alert.commodity_id]}</span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-mono text-zinc-200">{alert.commodity_name}</span>
                          <Badge 
                            variant="outline" 
                            className={`rounded-none text-[9px] ${
                              alert.condition === "above" ? "border-[#00ff41] text-[#00ff41]" :
                              alert.condition === "below" ? "border-[#ff3b30] text-[#ff3b30]" :
                              "border-[#0ea5e9] text-[#0ea5e9]"
                            }`}
                          >
                            {alert.condition === "above" ? "Above" : alert.condition === "below" ? "Below" : "Crosses"} ${alert.threshold.toLocaleString()}
                          </Badge>
                          {alert.triggered && (
                            <Badge className="rounded-none text-[9px] bg-[#eab308]/20 text-[#eab308] border-[#eab308]">
                              Triggered!
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-[10px] text-zinc-500 font-mono">
                            Current: <span className={currentPrices[alert.commodity_id] > alert.threshold ? "text-[#00ff41]" : "text-[#ff3b30]"}>
                              ${currentPrices[alert.commodity_id]?.toLocaleString() || "..."}
                            </span>
                          </span>
                          {alert.note && (
                            <span className="text-[10px] text-zinc-600 font-mono truncate">
                              Note: {alert.note}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {alert.triggered && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none border-zinc-700 h-8 w-8 p-0"
                          onClick={() => handleResetAlert(alert.alert_id)}
                          title="Reset alert"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Switch
                        checked={alert.active}
                        onCheckedChange={() => handleToggleAlert(alert.alert_id)}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-zinc-700 h-8 w-8 p-0 text-[#ff3b30] hover:bg-[#ff3b30]/10"
                        onClick={() => handleDeleteAlert(alert.alert_id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Settings className="w-3.5 h-3.5 text-[#00ff41]" />
                Notification Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <NotificationSettings />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
